import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  CircularProgress,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import toast from 'react-hot-toast';
import { ZoomIn } from '@mui/icons-material';
import { Order, Product } from '../../../interface/interface';
import { Variant } from '../../../interface/interface';
import { Address } from '../../../interface/interface';
import { Buyer, FeedbackMessage } from '../../../interface/interface';
import { API } from '../../../config/config';
import { useAuth } from 'src/hooks/useAuth';
import LogoSpinner from 'src/components/LogoSpinner';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { DeleteDialog } from '../../../components/DynamicTable/DynamicTable';
import { useUpdateModalStore } from 'src/zustandStore/store.update';

import {
  DynamicTable,
  TableSearch
} from '../../../components/DynamicTable/DynamicTable';
import { useOrder } from '../../../hooks/useOrder';
import { CheckoutOrder, Orders, OrdersResponse } from './orders.interface';

interface OrdersContentProps {
  orders: Orders[];
  loading: boolean;
  setOrders: React.Dispatch<React.SetStateAction<Orders[]>>;
  currentOrder: Orders | null;
  setCurrentOrder: React.Dispatch<React.SetStateAction<Orders | null>>;
  statusOptions: string[];
  updateOrderStatus: (orderId: string, newStatus: string) => Promise<void>;
  page: number;
  rowsPerPage: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  totalOrders: number;
  setTotalOrders: React.Dispatch<React.SetStateAction<number>>;
  onRefresh?: () => void;
}

const selectOrder = (order) => {};

const renderPillCount = (orders: [{ status: string }]) => {
  console.log(orders);
  const statusMap = new Map<String, number>();
  orders.forEach((order) => {
    const status = order.status;
    if (statusMap.has(status)) {
      statusMap.set(status, statusMap.get(status) + 1);
    } else {
      statusMap.set(status, 1);
    }
  });
  const set = new Set<string>(orders.map((order) => order.status));
  // Remove duplicates from the set
  return (
    <>
      {Array.from(set).map((status) => (
        <Box
          sx={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center'
          }}
        >
          <Typography variant="body2">{status.replace(/_/g, ' ')}</Typography>
          <Box
            sx={{
              backgroundColor: '#e0e0e0',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '0.75rem',
              lineHeight: 'normal'
            }}
          >
            {statusMap.get(status)}
          </Box>
        </Box>
      ))}
    </>
  );
};

export const OrderContent: FC<OrdersContentProps> = ({
  orders,
  loading,
  setOrders,
  currentOrder,
  setCurrentOrder,
  statusOptions,
  updateOrderStatus,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  totalOrders,
  setTotalOrders,
  onRefresh
}) => {
  const { enhancedFetch } = useAuth();
  const [searchData, setSearchData] = useState<Order[] | null>(null);
  const [message, setMessage] = useState<string>('');
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);
  const [feedbackMsg, setFeedbackMsg] = useState([]);
  const [deletedMessageIds, setDeletedMessageIds] = useState<string[]>([]);
  const [selectedProductOrder, setSelectedProductOrder] =
    useState<CheckoutOrder | null>(null);
  const updateModalStore = useUpdateModalStore((state) => state);
  const { fetchFeedbackMessages, deleteFeedbackMessage } = useOrder();
  const sortedOrdersAlphabetically = [...orders].sort((a, b) => {
    if (a.id > b.id) return 1;
    if (a.id < b.id) return -1;
    return 0;
  });
  const [refresh, setRefresh] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedProductOrder) return;
    fetchFeedbackMessages(selectedProductOrder?.orderId.toString() || '').then(
      (response) => {
        if (response instanceof Response) {
          setFeedbackMsg([]);
        } else {
          setFeedbackMsg(response as FeedbackMessage[]);
        }
      }
    );
  }, [selectedProductOrder]);

  useEffect(() => {
    if (!currentOrder) return;
    selectCheckoutOrder(currentOrder as { id: string });
  }, [refresh]);

  const selectCheckoutOrder = (order: { id: string }) => {
    enhancedFetch(`${API.HOST}/api/v1/checkout-sessions/${order.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }).then((data) => {
      // Set the selected product order
      setCurrentOrder(data);
    });
  };

  const handleSendMessage = (orderId: string) => {
    if (!currentOrder || !message.trim()) return;
    setIsSendingMessage(true);
    enhancedFetch(`${API.HOST}/api/v1/orders/${orderId}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orderId: orderId,
        message: message.trim(),
        fromBuyer: false
      })
    })
      .then((data) => {
        toast.success('Message sent successfully');
        setMessage('');
        fetchFeedbackMessages(orderId.toString()).then((response) => {
          //Set the feeedback messages
          if (response instanceof Response) {
            setFeedbackMsg([]);
          } else {
            setFeedbackMsg(response as FeedbackMessage[]);
          }
        }); // Call the function to fetch messages directly
      })
      .catch((error) => {
        console.error('Error sending message:', error);
        toast.error('Failed to send message');
      })
      .finally(() => {
        setIsSendingMessage(false);
      });
  };

  if (loading)
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LogoSpinner />
      </div>
    );

  return (
    <Card>
      <TableContainer>
        <DynamicTable
          showPagination={true}
          rowsPerPage={rowsPerPage}
          page={page}
          totalPage={totalOrders}
          searchElement={
            <TableSearch
              placeholder="Search Orders"
              searchData={orders}
              setSearchData={setSearchData}
            />
          }
          rows={orders.map((order) => ({
            id: order.id,
            buyerName: order.buyer?.name || 'N/A',
            totalAmount: `₦${
              order.totalAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) || '0.00'
            }`,
            totalPayableAmount: `₦${
              order.totalPayableAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) || '0.00'
            }`,
            createdAt: new Date(order.createdAt).toLocaleDateString(),
            actions: order,

            rowHeight: 80
          }))}
          headers={[
            // { headerName: 'Order ID', field: 'id' },
            { headerName: 'Order ID', field: 'id' },
            { headerName: 'Buyer', field: 'buyerName' },
            { headerName: 'Total Amount', field: 'totalAmount' },
            { headerName: 'Payable Amount', field: 'totalPayableAmount' },
            {
              headerName: 'Status',
              field: 'status',
              renderCell: ({ row: order }) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    justifyContent: 'center',
                    height: '100%'
                  }}
                >
                  {renderPillCount(order.actions.orders)}
                </Box>
              )
            },
            {
              headerName: 'Discounted Amount',
              field: 'totalDiscountAmount',
              renderCell: ({ row: order }) => (
                <>
                  <Typography
                    sx={{
                      p: '2%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {order.actions.totalDiscountAmount <= 0
                      ? 'N/A'
                      : `₦${order.actions.totalDiscountAmount.toLocaleString(
                          'en-US',
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }
                        )}`}
                  </Typography>
                </>
              )
            },
            { headerName: 'Created', field: 'createdAt' },

            {
              headerName: 'Actions',
              field: 'actions',
              renderCell: ({ row: order }) => (
                <IconButton
                  onClick={() => {
                    selectCheckoutOrder(order.actions);
                  }}
                  color="inherit"
                  size="small"
                >
                  <ZoomIn fontSize="small" />
                </IconButton>
              )
            }
          ]}
          handleChangePage={(event, newPage) => setPage(newPage)}
          handleChangeRowsPerPage={(event) => setRowsPerPage(event)}
        />
      </TableContainer>

      {/* Order Details Dialog */}
      <Dialog
        maxWidth="lg"
        fullWidth={true}
        open={!!currentOrder}
        onClose={() => setCurrentOrder(null)}
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '2%'
            }}
          >
            <Box>
              {currentOrder ? (
                <>
                  {/* Customer and Order Overview */}

                  <Typography variant="h6" gutterBottom>
                    RefCode ID: {currentOrder.checkoutRef}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    Created On:{' '}
                    {new Date(currentOrder.createdAt).toLocaleDateString()}
                  </Typography>

                  <div className="order-delivery-card">
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      className="order-delivery-info-item"
                    >
                      <span>Buyer:</span>
                      <span style={{}}></span>
                      <span style={{ textAlign: 'right' }}>
                        {currentOrder?.buyer?.name || 'N/A'}
                      </span>
                    </Typography>
                  </div>
                  {/* Items Table */}
                  <TableContainer sx={{ mb: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product Name</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Qty</TableCell>

                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentOrder ? (
                          currentOrder.orders.map((orderItem, index) => (
                            <TableRow
                              sx={{
                                background:
                                  selectedProductOrder?.orderId ===
                                  orderItem?.orderId
                                    ? '#1976d2'
                                    : 'transparent',
                                borderRadius:
                                  selectedProductOrder?.orderId ===
                                  orderItem?.orderId
                                    ? '8px'
                                    : '0px',
                                color:
                                  selectedProductOrder?.orderId ===
                                  orderItem?.orderId
                                    ? '#fff !important'
                                    : '#000',
                                transition: 'all 0.3s ease'
                              }}
                              key={currentOrder.id}
                            >
                              <TableCell
                                sx={{
                                  color:
                                    selectedProductOrder?.orderId ===
                                    orderItem?.orderId
                                      ? '#fff'
                                      : '#000'
                                }}
                              >
                                {orderItem.variant.title}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    selectedProductOrder?.orderId ===
                                    orderItem?.orderId
                                      ? '#fff'
                                      : '#000'
                                }}
                              >
                                {orderItem.amountPaid?.toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }) || '0.00'}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    selectedProductOrder?.orderId ===
                                    orderItem?.orderId
                                      ? '#fff'
                                      : '#000'
                                }}
                              >
                                {orderItem.units}
                              </TableCell>

                              <TableCell>
                                <IconButton
                                  onClick={() => {
                                    // Set the current Order to view details
                                    setSelectedProductOrder(orderItem);
                                  }}
                                  sx={{
                                    color:
                                      selectedProductOrder?.orderId ===
                                      orderItem?.orderId
                                        ? '#fff'
                                        : '#000'
                                  }}
                                  color="primary"
                                  size="small"
                                >
                                  <ZoomIn fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              No items available for this order.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                width: '2px',
                height: '100vh',
                borderLeft: '1px solid #efe8e8'
              }}
            ></Box>
            <Box
              sx={{
                width: 'auto',
                alignItems: 'center',
                height: '-webkit-fill-available',
                maxHeight: '100%'
              }}
            >
              {selectedProductOrder ? (
                <>
                  {/* Customer and Order Overview */}
                  <Typography variant="h6" gutterBottom>
                    RefCode ID: {selectedProductOrder.checkoutRef}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    Created On:{' '}
                    {new Date(
                      selectedProductOrder.createdAt
                    ).toLocaleDateString()}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                    <FormControl variant="outlined" size="small">
                      <InputLabel
                        id={`status-select-${selectedProductOrder.orderId}`}
                      >
                        Status
                      </InputLabel>

                      <Select
                        labelId={`status-select-${selectedProductOrder?.orderId}`}
                        id={`status-select-dropdown-${selectedProductOrder?.orderId}`}
                        value={selectedProductOrder.status}
                        onChange={(e) => {
                          setSelectedProductOrder((prev) => ({
                            ...prev,
                            status: e.target.value
                          }));
                        }}
                        label="Status"
                      >
                        {statusOptions?.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status.replace(/_/g, ' ')}
                          </MenuItem>
                        ))}
                      </Select>

                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          updateOrderStatus(
                            selectedProductOrder.orderId,
                            selectedProductOrder.status
                          ).then(() => {
                            // close the modal
                            // Find the order in the orders array and update its status
                            const updatedOrders = orders.map((order) => {
                              if (order.id === currentOrder?.id) {
                                return {
                                  ...order,
                                  status: selectedProductOrder.status
                                };
                              }
                              return order;
                            });
                            // Update the orders state with the updated orders
                            setOrders(updatedOrders);
                            // Show a success message
                            toast.success('Order status updated successfully');
                            // Close the current order
                            setRefresh(!refresh);

                            onRefresh();
                          });
                        }}
                        sx={{ mt: 1, minWidth: '100%' }}
                      >
                        Update Status
                      </Button>
                    </FormControl>
                  </Typography>
                  <div className="order-delivery-card">
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      className="order-delivery-info-item"
                    >
                      <span>Buyer:</span>
                      <span style={{}}></span>
                      <span style={{ textAlign: 'right' }}>
                        {currentOrder?.buyer?.name || 'N/A'}
                      </span>
                    </Typography>

                    {/* Vendor Details */}
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      className="order-delivery-info-item"
                    >
                      <span>Vendor:</span>

                      <span style={{}}></span>
                      <span style={{ textAlign: 'right' }}>
                        {selectedProductOrder?.variant?.vendor?.name || 'N/A'}
                      </span>
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      className="order-delivery-info-item"
                    >
                      <span>Vendor Company:</span>

                      <span style={{}}></span>
                      <span style={{ textAlign: 'right' }}>
                        {selectedProductOrder?.variant?.vendor?.company ||
                          'N/A'}
                      </span>
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      className="order-delivery-info-item"
                    >
                      <span>Vendor Contact:</span>

                      <span style={{}}></span>
                      <span style={{ textAlign: 'right' }}>
                        {selectedProductOrder?.variant?.vendor?.email || 'N/A'}
                      </span>
                    </Typography>

                    {/*   Delivery Address */}

                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      className="order-delivery-info-item"
                    >
                      <span>Delivery Address:</span>

                      <span style={{}}></span>
                      <span style={{ textAlign: 'right' }}>
                        {` ${selectedProductOrder?.address?.line1}, ${selectedProductOrder?.address?.city}, ${selectedProductOrder?.address?.state}, ${selectedProductOrder?.address?.country}`}
                      </span>
                    </Typography>
                  </div>
                  {/* Items Table */}
                  <TableContainer sx={{ mb: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product Name</TableCell>
                          <TableCell>Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentOrder ? (
                          <TableRow key={selectedProductOrder.orderId}>
                            <TableCell>
                              {selectedProductOrder.variant.title || 'N/A'}
                            </TableCell>

                            <TableCell>
                              ₦
                              {selectedProductOrder.variant.salePrice?.toLocaleString(
                                'en-US',
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }
                              ) || '0.00'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              No items available for this order.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TableContainer sx={{ mb: 1 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Order Summary</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell align={'right'} colSpan={3}>
                            Amount Paid
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentOrder ? (
                          <TableRow key={selectedProductOrder.orderId}>
                            <TableCell>
                              {selectedProductOrder.variant.title || 'N/A'}
                            </TableCell>
                            <TableCell>
                              {' '}
                              <span className="text-center">
                                {selectedProductOrder.units || '0'}
                              </span>
                            </TableCell>

                            <TableCell align={'right'}>
                              ₦
                              {selectedProductOrder?.amountPaid?.toLocaleString(
                                'en-US',
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }
                              ) || '0.00'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              No items available for this order.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* // text area with a button to send a message // the vendor has a
              messaging feature, render a textarea with a button to send a
              message // on button click, send a message to the vendor using the
              vendor's email */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ width: '100%', maxWidth: 1000, margin: '0 auto' }}
                  >
                    <TextField
                      label="Message to Buyer"
                      multiline
                      rows={4}
                      variant="outlined"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type your message here..."
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ width: '100%', maxWidth: 200, margin: '0 auto' }}
                      onClick={(e) =>
                        handleSendMessage(selectedProductOrder.orderId)
                      }
                      disabled={isSendingMessage || !message.trim()}
                    >
                      {isSendingMessage ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Box>

                  {/* Feedback */}

                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      mb: 2
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {selectedProductOrder?.note ||
                        'No notes available for this order.'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Feedback Messages
                    </Typography>

                    <Box
                      sx={{
                        padding: 2,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        mb: 2
                      }}
                    >
                      {feedbackMsg ? (
                        <Box
                          sx={{
                            padding: 2,
                            mb: 2,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}
                        >
                          {feedbackMsg?.map((msg: any) => (
                            <Box
                              key={msg.id}
                              sx={{
                                padding: 2,
                                mb: 2,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}
                            >
                              {/* Header: Date and Sender */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {new Date(msg.createdAt).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" color="primary">
                                  {msg.sender.name}
                                </Typography>
                              </Box>
                              {/* Message Content */}
                              <Typography variant="body2" color="textPrimary">
                                {msg.message}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No feedback available.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  {/*Create a simple empty state Overview*/}
                  <Typography variant="body1" color="textSecondary">
                    Select an order to view details.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCurrentOrder(null);
              setSelectedProductOrder(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
